<template>
  <div class="d-flex align-items-center flex-wrap gap-2">
    <div class="form-check mb-0">
      <input
        :id="`${uid}-auto-select`"
        v-model="enabled"
        class="form-check-input"
        type="checkbox"
        data-testid="auto-select-toggle"
      />
      <label class="form-check-label text-nowrap" :for="`${uid}-auto-select`">
        {{ $t("inventory_selection.auto_select") }}
      </label>
    </div>
    <div v-if="spreadLabel" class="form-check mb-0">
      <input
        :id="`${uid}-spread`"
        v-model="spread"
        class="form-check-input"
        type="checkbox"
        data-testid="spread-toggle"
      />
      <label class="form-check-label text-nowrap" :for="`${uid}-spread`">
        {{ spreadLabel }}
      </label>
    </div>
    <div class="target-mwh">
      <MwhInputComponent
        v-model="targetMwh"
        size="sm"
        :aria-label="$t('inventory_selection.target_label')"
        :placeholder="$t('inventory_selection.target')"
        data-testid="target-mwh"
      />
    </div>
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :disabled="!canClear"
      @click="$emit('clear')"
    >
      {{ $t("inventory_selection.clear") }}
    </button>
    <div
      class="d-flex align-items-center flex-wrap gap-2 quick-filters"
      data-testid="quick-filters"
    >
      <span class="toolbar-label text-uppercase text-body-tertiary">{{
        $t("inventory_selection.quick_filters")
      }}</span>
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useId } from "vue";

import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";

/**
 * A selection page's toolbar: auto-select with its target (and, given `spreadLabel`, the choice to
 * spread the target), Clear, then the page's quick filters in the default slot. The state lives
 * with the page (`useAutoSelect`), which knows what a fill selects from.
 */
defineProps<{
  canClear: boolean;
  /** Shows the spread checkbox, labelled with what the target is spread over. */
  spreadLabel?: string;
}>();

defineEmits<{
  clear: [];
}>();

const enabled = defineModel<boolean>("enabled", { required: true });
const targetMwh = defineModel<number | null>("targetMwh", { required: true });
const spread = defineModel<boolean>("spread", { default: true });

const uid = useId();
</script>

<style lang="scss" scoped>
.target-mwh {
  width: 150px;
}

// A thin rule between the selection controls and the filters, so the two groups stay legible.
.quick-filters {
  padding-left: 0.5rem;
  border-left: 1px solid var(--phoenix-border-color);
}

.toolbar-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
}
</style>
